<template>
    <div>
        <ManifestTable
            :rows="manifests"
            :searchTerm.sync="searchTerm"
            :page="page"
            :length="length"
            :total="total"
            :height="checkSystemSupport ? 'calc(100vh - 180px)' : 'calc(100vh - 140px)'"
            :loading="loading"
            :error="error"
            :filterSummary="filterSummary"
            @filter="openDateFilters"
            @search="searchManifests"
            @clear="clearSearch"
            @refresh="loadManifests"
            @page-change="changePage"
            @size-change="changeLength"
            @row-click="rowClick"
        >
           <!--  <template #connection>
                <el-radio-group v-if="checkSystemSupport" v-model="connection" class="mb-2" size="small">
                    <el-radio-button label="QATL_PRODUCTION">QATL_PRODUCTION</el-radio-button>
                    <el-radio-button label="QATL_DEV">QATL_DEV</el-radio-button>
                </el-radio-group>
            </template> -->
        </ManifestTable>
        <ManifestForm ref="moduleForm" @updated="loadManifests" />
        <el-dialog title="Incoming Containers - Filters" :visible.sync="filterDialog"
            :close-on-click-modal="false" :close-on-press-escape="true" width="min(560px, 95vw)"
            @close="cancelDateFilters">
            <form class="form-custom-format" @submit.prevent="applyDateFilters">
                <ThatsInput v-model="draftSearchTerm" label="Search" vid="manifest-filter-search"
                    placeholder="Manifest / Item / Container / UPC / Batch" maxlength="100"
                    labelClass="col-12" fieldClass="col-12" :show_error="false" :rules="''" size="sm" />
                <div class="row">
                    <div v-for="field in dateFilterFields" :key="field.key" class="col-sm-6">
                        <ThatsDatePicker :vid="'manifest-filter-' + field.key" v-model="draftFilters[field.key]"
                            :name="field.key" :label="field.label" :placeholder="field.label"
                            labelClass="col-12" fieldClass="col-12" :show_error="false" :rules="''" size="sm" />
                    </div>
                </div>
                <el-alert v-if="filterError" :title="filterError" type="error" :closable="false" show-icon />
            </form>
            <template #footer>
                <el-button @click="resetDateFilters">Reset Dates</el-button>
                <el-button @click="cancelDateFilters">Cancel</el-button>
                <el-button type="primary" @click="applyDateFilters">Apply</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import ManifestTable from './Table.vue';
import ManifestForm from './Form.vue';
import { ThatsDatePicker, ThatsInput } from '../../components/thats/Thats';
import { userData } from '../../helpers/user';

const emptyDateFilters = () => ({ shipDateFrom: '', shipDateTo: '', etaFrom: '', etaTo: '' });

export default {
    name: 'Manifest',
    components: { ManifestTable, ManifestForm, ThatsDatePicker, ThatsInput },
    data() {
        return {
            user: userData(),
            connection: 'QATL_PRODUCTION',
            searchTerm: '',
            appliedTerm: '',
            draftSearchTerm: '',
            filterDialog: false,
            filterError: '',
            draftFilters: emptyDateFilters(),
            appliedFilters: emptyDateFilters(),
            dateFilterFields: [
                { key: 'shipDateFrom', label: 'Ship Date - From' },
                { key: 'shipDateTo', label: 'Ship Date - To' },
                { key: 'etaFrom', label: 'ETA - From' },
                { key: 'etaTo', label: 'ETA - To' }
            ],
            manifests: [],
            page: 1,
            length: 25,
            total: 0,
            loading: false,
            error: '',
            requestId: 0
        };
    },
    computed: {
        filterSummary() {
            return [
                ['Ship Date', this.appliedFilters.shipDateFrom, this.appliedFilters.shipDateTo],
                ['ETA', this.appliedFilters.etaFrom, this.appliedFilters.etaTo]
            ].filter(([, from, to]) => from || to)
                .map(([label, from, to]) => `${label}: ${from || 'Any'} to ${to || 'Any'}`).join(' | ');
        },
        checkSystemSupport() {
            return this.$helpers.isSystemAdministrator() || (this.user.user && this.user.user.id === 424);
        },
        manifestConnection() {
            return this.connection === 'QATL_DEV' ? 'LISA_QATL_DEV' : 'LISA_QATL_PRODUCTION';
        }
    },
    mounted() {
        return this.loadManifests();
    },
    beforeDestroy() {
        this.requestId += 1;
    },
    watch: {
        connection() {
            this.$refs.moduleForm.closeDialog();
            return this.clearSearch();
        }
    },
    methods: {
        ...mapActions('ManifestModule', ['listManifests']),
        openDateFilters() {
            this.draftSearchTerm = this.appliedTerm;
            this.draftFilters = { ...this.appliedFilters };
            this.filterError = '';
            this.filterDialog = true;
        },
        cancelDateFilters() {
            this.draftSearchTerm = this.appliedTerm;
            this.draftFilters = { ...this.appliedFilters };
            this.filterError = '';
            this.filterDialog = false;
        },
        applyDateFilters() {
            this.filterError = '';
            const term = this.draftSearchTerm == null ? '' : this.draftSearchTerm;
            if (typeof term !== 'string' || term.length > 100) {
                this.filterError = 'Search must be at most 100 characters.';
                return;
            }
            for (const field of this.dateFilterFields) {
                const value = this.draftFilters[field.key];
                if (value === '' || value == null) continue;
                if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                    this.filterError = `${field.label} must be a valid date.`;
                    return;
                }
                const [year, month, day] = value.split('-').map(Number);
                const date = new Date(Date.UTC(year, month - 1, day));
                if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
                    this.filterError = `${field.label} must be a valid date.`;
                    return;
                }
            }
            for (const [from, to, label] of [
                ['shipDateFrom', 'shipDateTo', 'Ship Date'], ['etaFrom', 'etaTo', 'ETA']
            ]) {
                if (this.draftFilters[from] && this.draftFilters[to] && this.draftFilters[from] > this.draftFilters[to]) {
                    this.filterError = `${label}: From must not be after To.`;
                    return;
                }
            }
            this.appliedTerm = term.trim();
            this.searchTerm = this.appliedTerm;
            this.appliedFilters = { ...this.draftFilters };
            this.filterDialog = false;
            this.page = 1;
            this.total = 0;
            return this.loadManifests();
        },
        resetDateFilters() {
            this.draftSearchTerm = this.appliedTerm;
            this.draftFilters = emptyDateFilters();
            return this.applyDateFilters();
        },
        rowClick(row) {
            return this.$refs.moduleForm.openDialog(row, this.manifestConnection);
        },
        async loadManifests() {
            const requestId = ++this.requestId;
            const connection = this.manifestConnection;
            this.loading = true;
            this.error = '';
            this.manifests = [];
            try {
                const filters = {};
                this.dateFilterFields.forEach(field => {
                    if (this.appliedFilters[field.key]) filters[field.key] = this.appliedFilters[field.key];
                });
                const response = await this.listManifests({ connection, term: this.appliedTerm, page: this.page, length: this.length, ...filters });
                if (requestId !== this.requestId || connection !== this.manifestConnection) return;
                if (!response || !Array.isArray(response.data) || !response.meta || !Number.isFinite(Number(response.meta.total))) {
                    throw new Error('Invalid manifest list response');
                }
                this.manifests = response.data;
                this.total = Number(response.meta.total);
            } catch (error) {
                if (requestId !== this.requestId || connection !== this.manifestConnection) return;
                this.error = 'Unable to load manifests. Please try Refresh again.';
            } finally {
                if (requestId === this.requestId) this.loading = false;
            }
        },
        searchManifests() {
            if (this.searchTerm.length > 100) {
                this.error = 'Search must be at most 100 characters.';
                return;
            }
            this.appliedTerm = this.searchTerm.trim();
            this.page = 1;
            this.total = 0;
            return this.loadManifests();
        },
        clearSearch() {
            this.searchTerm = '';
            return this.searchManifests();
        },
        changePage(page) {
            this.page = page;
            return this.loadManifests();
        },
        changeLength(length) {
            this.length = length;
            this.page = 1;
            return this.loadManifests();
        }
    }
};
</script>
